import { useState, useContext, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Train } from 'lucide-react';
import TrainTrackingContext from '../../../../context/tracking/TrainTrackingContext';
import TrackingBanner from '../Shared/TrackingBanner';
import TrackingSearchBox from '../Shared/TrackingSearchBox';
import TrainImg from '../../../../assets/Background/train_bg.png';
import UpcomingTrainsTable from './sections/UpcomingTrainsTable';
import TrainTrackingResults from './sections/TrainTrackingResults';

const TrainTrackingPage = ({ hideHeader = false }) => {
  const { trackTrain, trainData, loading, error, clearTrainData } = useContext(TrainTrackingContext);
  const [searchParams] = useSearchParams();
  const [trainNumber, setTrainNumber] = useState(searchParams.get('train_number') || '');
  const [journeyDate, setJourneyDate] = useState(
    searchParams.get('journey_date') || new Date().toISOString().split('T')[0]
  );

  const currentStationRef = useRef(null);

  useEffect(() => {
    const paramTrain = searchParams.get('train_number');
    const paramDate = searchParams.get('journey_date');
    if (paramTrain) {
      setTrainNumber(paramTrain);
      if (paramDate) setJourneyDate(paramDate);
      trackTrain(paramTrain, paramDate || journeyDate);
    }
  }, [searchParams]);

  useEffect(() => {
    if (trainData && currentStationRef.current) {
      setTimeout(() => {
        currentStationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 1000);
    }
  }, [trainData]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (trainNumber) {
      trackTrain(trainNumber, journeyDate);
    }
  };

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <TrackingBanner
          title="Live Train Tracking"
          description="Track live train status, PNR status, and running information instantly. Keep your customers informed."
          image={TrainImg}
        />
      )}

      {/* Search Section */}
      <TrackingSearchBox
        fields={[
          {
            label: 'Train Number',
            placeholder: 'e.g. 12951 or 12009',
            value: trainNumber,
            onChange: (e) => setTrainNumber(e.target.value),
            icon: Train
          },
          {
            label: 'Journey Date',
            type: 'date',
            value: journeyDate,
            onChange: (val) => setJourneyDate(val)
          }
        ]}
        onSubmit={handleSearch}
        buttonText="Track"
        loading={loading}
        disabled={!trainNumber.trim()}
      />

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-xl mb-6 border border-red-200">
          {error}
        </div>
      )}

      {/* Live Tracking Result Card Section */}
      {trainData && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                clearTrainData();
                setTrainNumber('');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer active:scale-95 shadow-2xs"
            >
              <ArrowLeft size={14} /> Back to Upcoming Trains
            </button>
            <span className="text-xs text-slate-400 font-medium">Viewing live tracking details</span>
          </div>
          <TrainTrackingResults
            trainData={trainData}
            trainNumber={trainNumber}
            onClose={() => {
              clearTrainData();
              setTrainNumber('');
            }}
          />
        </div>
      )}

      {/* Upcoming Trains Table Section */}
      {!trainData && !loading && <UpcomingTrainsTable />}
    </div>
  );
};

export default TrainTrackingPage;
